<template>
  <div class="w-full account-address-sector">
    <el-radio-group v-model="form.fromExisted" :label="label" @change="chooseFromExisted">
      <el-radio v-if="localState.uadsList.length > 0" :label="1" size="large" class="w-full mr-0">
        <div class="w-full">
          <div class="d-flex justify-content-between align-items-end">
            <span class="font-weight-bold text-base">
              {{ type === 'address' ? $t('從最近地址挑選') : $t('從最近門市挑選') }}
            </span>
            <span>
              <a v-if="form.fromExisted === 1" href="javascript:void(0)" class="text-accent font-weight-medium"
                @click="visibleReselectUadsDialog()">
                {{ $t('重新挑選') }}
              </a>
            </span>
          </div>
          <div class="text-gray-400 text-sm">
            <div v-if="selectedUads?.uads_id" class="text-gray-600">
              <span class="font-weight-medium text-sm">{{ selectedUads?.to_name }}</span>
              <span class="ml-1" style="font-size: 0.9rem;">{{ mobileE164PrettyPrint(selectedUads?.to_mobile || '')
              }}</span>
            </div>
            <template v-if="selectedUads?.uads_type === 'address'">
              <span class="text-gray-400 text-sm">
                <span v-if="selectedUads?.postcode">({{ selectedUads?.postcode }})</span>
                {{ selectedUads?.province }} {{ selectedUads?.city }}
              </span>
              <span class="text-sm text-pretty ml-1">{{ selectedUads?.address }}</span>
            </template>
            <template
              v-else-if="['cvs_pcsc', 'cvs_family', 'cvs_hilife', 'cvs_okmart'].includes(selectedUads?.uads_type)">
              <span class="text-gray-500 font-weight-medium text-sm">
                {{ selectedUads?.uads_type === 'cvs_pcsc' ? `${selectedUads?.shop?.shop_name}${$t('門市')}` :
                  `${selectedUads?.shop?.shop_name}` }}
              </span>
              <span>{{ selectedUads?.shop?.shop_address_full }}</span>
            </template>
          </div>
        </div>
      </el-radio>
      <el-radio :label="-1" size="large" class="w-full mr-0">
        <span class="block font-weight-bold text-base">{{ type === 'address' ? $t('新增寄送地址') : $t('新增取貨門市') }}</span>
      </el-radio>
    </el-radio-group>
    <el-dialog v-model="localState.reselectUads.visible" :title="`${type === 'address' ? $t('最近地址') : $t('最近門市')}`"
      :width="isMobile ? '100%' : '600px'" :fullscreen="isMobile" :close-on-click-modal="false">
      <el-scrollbar>
        <el-radio-group v-model="form.uads_id" @change="(val) => { chooseAddress(val); hideReselectUadsDialog(); }"
          class="w-full">
          <el-radio v-for="uads in localState.uadsList" :key="`uads_${uads.uads_id}`" :label="uads.uads_id" size="large"
            class="w-full mx-2 my-1 py-2 px-3 border border-dashed border-slate-300">
            <div class="text-gray-400">
              <div>
                <span class="font-weight-medium text-accent text-base">{{ uads?.to_name }}</span>
                <span class="ml-1" style="font-size: 0.9rem;">{{ mobileE164PrettyPrint(uads?.to_mobile || '') }}</span>
                <el-popconfirm v-if="uads.uads_id !== form.uads_id" title="確定要刪除此地址/門市嗎？"
                  @confirm="() => handleRemove(uads.uads_id)">
                  <template #reference>
                    <el-button type="danger" :icon="Delete" size="small" class="ml-2" circle></el-button>
                  </template>
                </el-popconfirm>
              </div>
              <template v-if="uads?.uads_type === 'address'">
                <div style="font-size: 0.9rem;">
                  <span v-if="uads?.postcode">({{ uads.postcode }})</span>
                  {{ uads?.province }} {{ uads?.city }}
                </div>
                <div class="text-pretty" style="font-size: 0.9rem;">{{ uads?.address }}</div>
              </template>
              <template v-else-if="['cvs_pcsc', 'cvs_family', 'cvs_hilife', 'cvs_okmart'].includes(uads?.uads_type)">
                <div class="text-accent text-sm">
                  <span v-if="uads?.uads_type === 'cvs_pcsc'">{{ `${uads?.shop?.shop_name}${$t('門市')}` }}</span>
                  <span v-else>{{ uads?.shop?.shop_name }}</span>
                </div>
                <div class="text-pretty" style="font-size: 0.825rem;">
                  {{ uads?.shop?.shop_address_full }}
                </div>
              </template>
            </div>
          </el-radio>
        </el-radio-group>
      </el-scrollbar>
    </el-dialog>
  </div>
</template>

<script setup>
import { reactive, onMounted, ref, computed } from "vue";
import { useStore } from 'vuex'
import { useI18n } from "vue-i18n"
import { notificationErrors, mobileE164PrettyPrint, breakpoint } from "../../../utils";
import {
  _api_getAddresses,
  _api_postAddress,
  _api_deleteAddress,
  _api_setDefaultAddress,
} from "../../../api/index.js";
import { useVModel } from "@vueuse/core";
import { keyBy } from "lodash";
import { Delete } from '@element-plus/icons-vue'

const props = defineProps({
  modelValue: Object,
  type: {
    type: String,
    default: null,
  },
});
const emit = defineEmits(["update:modelValue", "ok"]);

const { t, n, locale } = useI18n()
const store = useStore()

const form = useVModel(props, "modelValue", emit);

const isMobile = computed(() => breakpoint.value.xs || breakpoint.value.sm)

const localState = reactive({
  uadsList: [],
  reselectUads: {
    visible: false,
  },
});

const label = computed(() => {
  if (props.type == "cvs_pcsc") {
    return t("orders.shop711");
  } else if (props.type == "cvs_family") {
    return t("orders.shopFami");
  } else if (props.type == "cvs_hilife") {
    return t("orders.shopHilife");
  } else if (props.type == "cvs_okmart") {
    return t("orders.shopOkmart");
  }
  return t("orders.shippingAddress");
});

const selectedUads = computed(() => {
  const { fromExisted, uads_id } = form.value;
  if (fromExisted === -1 || !uads_id) {
    return {};
  }
  const listMapByUadsId = keyBy(localState.uadsList, "uads_id");
  return {
    ...(listMapByUadsId?.[uads_id] || {}),
  };
});

const getAccountAddresses = async () => {
  store.dispatch("lite/setFullPageLoading", true);
  try {
    const resp = await _api_getAddresses({
      uads_type: props.type,
      fields: "shop,is_outer_island",
    });
    localState.uadsList = resp.data;
    if (props.type === "address") {
      // 國家過濾
      localState.uadsList = localState.uadsList.filter(
        (el) => el?.iso_country == props.modelValue.country
      );
    }
    if (!form.value.isOuterIsland) {
      // 不允許外島
      localState.uadsList = localState.uadsList.filter(
        (el) => !el?.is_outer_island
      );
    }
    // console.warn("getAccountAddresses: ", localState.uadsList);
    let defaultItem = localState.uadsList.find(
      (el) => el.is_default && !el?.is_outer_island
    );
    if (!defaultItem) {
      defaultItem = localState.uadsList.find((el) => !el?.is_outer_island);
    }
    // console.warn("defaultItem: ", defaultItem);
    if (defaultItem) {
      setTimeout(() => {
        form.value.fromExisted = 1;
        form.value.uads_id = defaultItem.uads_id;
        chooseAddress(defaultItem.uads_id);
      }, 100);
    }
  } catch (err) {
    notificationErrors(err);
  } finally {
    store.dispatch("lite/setFullPageLoading", false);
  }
};

const chooseFromExisted = (val) => {
  if (val === -1) {
    chooseAddress(-1);
    return;
  }
  if (form.value.uads_id === -1 && localState.uadsList.length) {
    form.value.uads_id = localState.uadsList?.[0]?.uads_id;
  }
  chooseAddress(form.value.uads_id);
};

const chooseAddress = (val) => {
  if (val === -1) {
    emit("ok");
    return;
  }
  const choose = localState.uadsList.find((address) => address.uads_id == val);
  // console.warn("choose", choose);
  emit("ok", choose);
};

const handleCreate = async (data) => {
  console.warn("handleCreate: ", props.type, data);
  if (!props.type || !data) {
    return;
  }
  store.dispatch("lite/setFullPageLoading", true);
  try {
    const uadsType = props.type;
    const uadsData = {
      uads_type: uadsType,
      to_name: data.to_name,
      to_mobile: data.to_mobile,
      // to_mail: data.to_email,
      country: data.country,
    };
    if (uadsType == "address") {
      // const { countryMap } = data;
      const iso_province = data.province;
      const iso_city = data.city;
      const iso_region = data.region;
      // const province = countryMap.province?.[iso_province]?.name;
      // const city = countryMap.province?.[iso_province]?.city?.[iso_city]?.name;
      // const region =
      //   countryMap.province?.[iso_province]?.city?.[iso_city]?.region?.[
      //     iso_region
      //   ]?.name;
      Object.assign(uadsData, {
        province: iso_province,
        city: iso_city,
        region: iso_region,
        // province,
        // city,
        // region,
        postcode: data.postal_code,
        address: data.address,
      });
    } else if (uadsType == "cvs_family") {
      Object.assign(uadsData, {
        cvs_family_shop_id: data.cvs_family_shop_id,
      });
    } else if (uadsType == "cvs_pcsc") {
      Object.assign(uadsData, {
        cvs_711_shop_id: data.cvs_711_shop_id,
      });
    } else if (uadsType == "cvs_hilife") {
      Object.assign(uadsData, {
        cvs_hilife_shop_id: data.cvs_hilife_shop_id,
      });
    } else if (uadsType == "cvs_okmart") {
      Object.assign(uadsData, {
        cvs_okmart_shop_id: data.cvs_okmart_shop_id,
      });
    } else {
      return;
    }
    const resp = await _api_postAddress(uadsData);
    return resp.data;
  } catch (err) {
    notificationErrors(err);
  } finally {
    store.dispatch("lite/setFullPageLoading", false);
  }
};

const handleDefault = (uads_id) => {
  // 結帳時才會觸發，強制將選用的地址設為預設
  try {
    _api_setDefaultAddress(uads_id);
  } catch (err) {
    notificationErrors(err);
  }
};

const handleRemove = async (uads_id) => {
  store.dispatch('lite/setFullPageLoading', true);
  try {
    await _api_deleteAddress(uads_id);
    localState.uadsList = localState.uadsList.filter((el) => el.uads_id !== uads_id);
  } catch (err) {
    notificationErrors(err);
  } finally {
    store.dispatch('lite/setFullPageLoading', false);
  }
};

const visibleReselectUadsDialog = () => {
  localState.reselectUads.visible = true;
};

const hideReselectUadsDialog = () => {
  localState.reselectUads.visible = false;
};

onMounted(() => {
  getAccountAddresses();
});

defineExpose({ handleCreate, handleDefault });
</script>

<style lang="scss">
.account-address-sector {
  .el-radio-group {
    width: 100%;

    .el-radio {
      height: inherit;

      .el-radio__input {
        width: 20px;
      }
    }
  }
}
</style>
